import { HttpClient, HttpContext, HttpRequest } from '@angular/common/http';
import { Injectable, isDevMode } from '@angular/core';

import { BehaviorSubject, Observable, delay, finalize, map, retryWhen, scan, takeUntil, tap } from 'rxjs';
import { OVERRIDE_CTX } from 'src/app/core/interceptors/http-error.interceptor';
import { ToastService } from 'src/app/core/services/toast.service';
import { environment } from 'src/environments/environment';

import { LoadSpinnerService } from '../spinner/loadspinner.service';

@Injectable({ providedIn: 'root' })
export class ApiloaderService {
  private loadingSubject = new BehaviorSubject<boolean>(false);

  //WITH DEBOUNCE TO DELAY SHOW
  // loading$ = this.loadingSubject.asObservable().pipe(
  //   debounceTime(200),
  //   distinctUntilChanged()
  // );

  public readonly loading$ = this.loadingSubject.asObservable(); //No delayed show

  private activeRequests$ = new BehaviorSubject<HttpRequest<any>[]>([]);
  private requestTimes = new Map<HttpRequest<any>, number>();
  isOffline = new BehaviorSubject<boolean>(false);
  public readonly isOffline$ = this.isOffline.asObservable();
  isAPIOffline = new BehaviorSubject<boolean>(false);
  public readonly isAPIOffline$ = this.isAPIOffline?.asObservable();

  isLoading = new BehaviorSubject<boolean>(false);
  isLoading$ = this.isLoading?.asObservable();

  private reconnectionPromise: Promise<boolean> | null = null;

  constructor(public loaderService: LoadSpinnerService, private http: HttpClient, private toastService: ToastService) {
    this.updateOnlineStatus();
    window.addEventListener('online', this.updateOnlineStatus.bind(this));
    window.addEventListener('offline', this.updateOnlineStatus.bind(this));
  }


  getServerAvailable() {
    return this.http.get(environment.apiUrl + '/loan/api/admin/is-available', {
      context: new HttpContext().set(OVERRIDE_CTX, true),
    });
  }

  private updateOnlineStatus(): void {
    this.isOffline.next(!window.navigator.onLine);
    console.info(`isOnline=[${window.navigator.onLine}]`);
  }

  public setAPIStatus(currentstatus: boolean): void {
    this.isAPIOffline.next(!currentstatus);
    console.info(`APIStatus=[${currentstatus}]`);
  }

  WaitforOnline(lastreq?: HttpRequest<any>): Promise<boolean> {
    // If a check is already in progress, return the existing promise
    if (this.reconnectionPromise) {
      return this.reconnectionPromise;
    }

    this.reconnectionPromise = new Promise<boolean>((resolve, reject) => {
      const loadingService = this.loaderService;
      loadingService.startLoading('Attempting reconnect, please wait!');

      this.getServerAvailable().pipe(
        map((serverres: any) => {
          if (!(serverres?.status as string)?.startsWith('OK')) {
            throw new Error('Server returned non-OK status');
          }
          return serverres;
        }),
        retryWhen((errors) =>
          errors.pipe(
            takeUntil(this.isOffline), // will stop retries if offline state is emitted
            tap(() => console.warn('Retrying server request...')),
            delay(10000),
            scan((retryCount) => {
              if (retryCount >= 4) {
                throw new Error('Max retries reached');
              }
              return retryCount + 1;
            }, 0)
          )
        ),
        takeUntil(this.isOffline),
        finalize(() => loadingService.stopLoading())
      ).subscribe({
        next: () => {
          this.isAPIOffline.next(false);
          this.isOffline.next(false);
          if (lastreq) this.removeRequest(lastreq);
          this.reconnectionPromise = null;
          resolve(true);
        },
        error: (err) => {
          this.isAPIOffline.next(true); // signal to show offline image
          this.isOffline.next(true); // halt other streams with takeUntil
          this.reconnectionPromise = null;
          reject(new Error(err.message || 'Failed to reconnect to the server'));
        }
      });

      return this.reconnectionPromise;
    });
    return this.reconnectionPromise;
  }

  show() {
    this.loadingSubject.next(true);
  }

  hide() {
    this.loadingSubject.next(false);
  }

  setRequest = (req: HttpRequest<any>) => {
    const current = this.activeRequests$.getValue();
    this.activeRequests$.next([...current, req]);
    this.requestTimes.set(req, performance.now());

    if (isDevMode()) {
      console.info('LoaderService[DevModeOnly]: ', req.url);
    }
    this.show();
    return req;
  };

  removeRequest = (req: HttpRequest<any>) => {
    const startTime = this.requestTimes.get(req);
    if (startTime !== undefined) {
      this.logRequestTime(startTime, req.url, req.method);
      this.requestTimes.delete(req);
    }

    const current = this.activeRequests$.getValue();
    const next = current.filter(r => r !== req);
    this.activeRequests$.next(next);

    if (next.length === 0) {
      this.hide();
    }
  };

  getHasRequestsExceptCurrent$(request: HttpRequest<any>): Observable<boolean> {
    return this.activeRequests$.asObservable().pipe(
      map(requests => requests.filter(r => r !== request).length > 0)
    );
  }

  private logRequestTime(startTime: number, url: string, method: string) {
    const requestDuration = `${performance.now() - startTime}`;
    console.info(`[${method}] ${url}: ${requestDuration} msecs`);
  }
}
